import React, {useState, useEffect, useRef} from 'react';
import {API_ENDPOINTS} from "../../../apiConfig";
import API from "../../../utils/API";
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import Forms from "../../../components/Forms";
import {Chip} from "@mui/material";
import DeleteIconButton from "../../../components/Forms/DeleteIconButton";
import EditIconButton from "../../../components/Forms/EditIconButton";
import RepairModal from "../../../components/RepairModal";
import {formatPrice} from "../../../utils/tools";
import {useLocation, useNavigate} from 'react-router-dom';
import routes from "../../../routesConfig";
import {useModal} from "../../../contexts/ModalContext";
import AdminDataView from "../../../components/AdminDataView";

const Repairs = () => {
    const [repairs, setRepairs] = useState([]);
    const [filteredRepairs, setFilteredRepairs] = useState([]);
    const [filterText, setFilterText] = useState('');
    const {t} = useTranslation();
    const [error, setError] = useState();
    const navigate = useNavigate();
    const location = useLocation();
    const {openModal, confirm, stack} = useModal();

    const filterInputRef = useRef(null);
    const autoOpenedBuybackRef = useRef(null);

    const getWorkedMinutesTotal = (repair) => {
        if (typeof repair.workedMinutesTotal === 'number') {
            return repair.workedMinutesTotal;
        }
        return (repair.repairs || []).reduce((sum, deviceRepair) => {
            return sum + (deviceRepair.repairList || []).reduce((taskSum, task) => taskSum + (Number(task.workedMinutes) || 0), 0);
        }, 0);
    };

    const hasActiveWork = (repair) => {
        if (typeof repair.activeWork === 'boolean') {
            return repair.activeWork;
        }
        return (repair.repairs || []).some((deviceRepair) =>
            (deviceRepair.repairList || []).some((task) =>
                (task.workSessions || []).some((session) => !session.endedAt)));
    };

    const getOwnerName = (repair) => {
        if (repair.customer) {
            return repair.customer.type === 'person'
                ? `${repair.customer.person?.name || ''} ${repair.customer.person?.surname || ''}`.trim()
                : repair.customer.company?.name || '-';
        }
        if (repair.buyback) {
            const buybackLabel = repair.buyback.buybackNumber || repair.buyback._id || String(repair.buyback);
            return `Výkup ${buybackLabel || ''}`.trim();
        }
        return 'Interní zařízení';
    };

    const fetchRepairs = async () => {
        const response = await API.get(API_ENDPOINTS.repairs.list);
        const repairs = response.data.map(item => ({...item, key: item.id}));
        setRepairs(repairs);
        setFilteredRepairs(repairs);
        setFilterText('');
    };

    useEffect(() => {
        fetchRepairs();
    }, []);

    useEffect(() => {
        const filtered = repairs.filter((repair) => {
            const filterLowerCase = filterText.toLowerCase();
            return getOwnerName(repair).toLowerCase().includes(filterLowerCase);
        });
        setFilteredRepairs(filtered);
    }, [filterText, repairs]);

    useEffect(() => {
        if (stack.length > 0) return;

        const handleKeyDown = (e) => {
            if (!e.shiftKey && filterInputRef.current && document.activeElement !== filterInputRef.current) {
                filterInputRef.current.focus();
            }

            if (e.shiftKey && e.key === 'N') {
                e.preventDefault();
                openNewRepairModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [stack.length]);

    const handleFilterKeyDown = (e) => {
        if (e.key === 'Escape') {
            setFilterText('');
        }
    };

    const confirmDeleteRepair = async (repair) => {
        const result = await confirm(`Opravdu chcete zrušit opravu ${repair._id}?`);
        if (result === true) {
            try {
                await API.delete(API_ENDPOINTS.repairs.delete(repair._id));
                setRepairs(repairs.filter(r => r._id !== repair._id));
            } catch (error) {
                setError(error);
                console.error("Failed to delete repair:", error);
            }
        }
    };

    const columns = [
        {
            key: 'name',
            name: t('customerName'),
            renderCell: ({row}) => <label>{getOwnerName(row)}</label>
        },
        {
            key: 'dueDate',
            name: t('repairs.dueDateLabel'),
            renderCell: ({row}) => row.dueDate ? moment(row.dueDate).format('DD.MM.YYYY') : '-'
        },
        {
            key: 'repairsCount',
            name: t('repairs.repairsCount'),
            renderCell: ({row}) => {
                const repairInfo = row.repairs.reduce((acc, repair) => {
                    acc.deviceCount++;
                    acc.deviceRepairCount += repair.repairList.length;
                    return acc;
                }, {deviceCount: 0, deviceRepairCount: 0});
                return `${repairInfo.deviceCount} (${repairInfo.deviceRepairCount})`;
            }
        },
        {
            key: 'status',
            name: t('status'),
            renderCell: ({row}) => t(`repairStatus.${row.status}`)
        },
        {
            key: 'activeWork',
            name: 'Aktivní práce',
            renderCell: ({row}) => hasActiveWork(row)
                ? <Chip size="small" color="success" label="Ano"/>
                : <Chip size="small" variant="outlined" label="Ne"/>
        },
        {
            key: 'workedMinutesTotal',
            name: 'Odpracováno',
            renderCell: ({row}) => `${getWorkedMinutesTotal(row)} min`
        },
        {
            key: 'estimatedPrice',
            name: t('estimatedPrice'),
            renderCell: ({row}) => formatPrice(row.estimatedPrice)
        },
        {
            key: 'createdBy',
            name: t('createdBy'),
            renderCell: ({row}) => `${row.createdBy.name} ${row.createdBy.surname}`
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            renderCell: ({row}) => moment(row.createdAt).format('DD.MM.YYYY HH:mm')
        },
        {
            key: 'actions',
            width: 60,
            name: 'Akce',
            renderCell: ({row}) => {
                return (
                    <div className="flex justify-end items-center h-full w-full">
                        <EditIconButton title="Upravit opravu" onClick={() => navigate(routes.admin.getEditRepair(row._id))}/>
                        <DeleteIconButton title="Smazat opravu" onClick={() => confirmDeleteRepair(row)}/>
                    </div>
                );
            }
        },
    ].map(column => ({...column, resizable: true}));

    const openNewRepairModal = () => {
        openModal(RepairModal, {onRepairUpdate: fetchRepairs}, {title: 'test'});
    };

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const buybackId = params.get('buybackId');
        if (!buybackId) return;
        if (autoOpenedBuybackRef.current === buybackId) return;
        autoOpenedBuybackRef.current = buybackId;

        openModal(
            RepairModal,
            {
                onRepairUpdate: fetchRepairs,
                initialBuybackId: buybackId
            },
            {title: 'Nová oprava z výkupu'}
        );
        navigate(routes.admin.repairsFull, {replace: true});
    }, [location.search]);

    return (
        <div>
            <Forms.Button label={t('repairs.addRepair')} onClick={() => openNewRepairModal()} className="mb-8"/>

            <div className="grid grid-cols-4 items-end gap-4 mb-4">
                <Forms.Text
                    ref={filterInputRef}
                    value={filterText}
                    onChange={val => setFilterText(val)}
                    onKeyDown={handleFilterKeyDown}
                    label={t('name')}
                    placeholder={t('searchByName')}
                />
            </div>
            <AdminDataView
                section="repairs"
                rows={filteredRepairs}
                columns={columns}
            />
        </div>
    );
};

export default Repairs;
